import api from "api/axios";

export type SharePermission = "view" | "edit";

export interface DocumentCategory {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

// List-view shape — no `content`, so listing documents doesn't ship every
// document's full HTML body just to render a card.
export interface DocumentSummary {
  id: number;
  title: string;
  description: string | null;
  category_id: number | null;
  tags: string[];
  is_active: boolean;
  is_readonly: boolean;
  created_at: string | null;
  updated_at: string | null;
  /** Only present on the employee-router response. Admin has no share row,
   * so treat undefined as full access when the caller is an admin. */
  my_permission?: SharePermission;
}

// Full shape, including `content` — used for a single-document fetch.
export interface HrmsDocument extends DocumentSummary {
  content: string;
  version: number;
}

export interface DocumentShare {
  id: number;
  document_id: number;
  employee_id: number;
  employee_code: string;
  employee_name: string;
  permission: SharePermission;
  created_at: string | null;
}

export interface DocumentCheckpoint {
  id: number;
  document_id: number;
  label: string | null;
  version_at_checkpoint: number;
  created_at: string | null;
}

export interface AssignableEmployee {
  id: number;
  employee_code: string;
  name: string;
}

export interface SimilarDocumentMatch {
  id: number;
  title: string;
  category_id: number | null;
  ratio: number;
}

// Distinct from SimilarDocumentMatch — a DocumentCategory is keyed by `name`,
// not `title`, so its own similarity-check shape differs.
export interface SimilarCategoryMatch {
  id: number;
  name: string;
  ratio: number;
}

export interface ListResponse<T> {
  items: T[];
  total_count: number;
  total_pages: number;
  page: number;
}

export interface DocumentFilters {
  search?: string;
  category_id?: number;
  tag?: string;
  page?: number;
  page_size?: number;
}

function documentsApiBase(): string {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/documents" : "/employee/documents";
}

function isAdminBase(): boolean {
  return documentsApiBase() === "/admin/documents";
}

// ── Documents ────────────────────────────────────────────────────────────

export async function listDocuments(filters: DocumentFilters = {}): Promise<ListResponse<DocumentSummary>> {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.category_id !== undefined) params.set("category_id", String(filters.category_id));
  if (filters.tag) params.set("tag", filters.tag);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 20));
  const res = await api.get(`${documentsApiBase()}?${params.toString()}`);
  return res.data;
}

export async function getDocument(id: number): Promise<HrmsDocument> {
  const res = await api.get(`${documentsApiBase()}/${id}`);
  return res.data;
}

export async function createDocument(data: {
  title: string;
  description?: string;
  category_id?: number | null;
  tags?: string[];
}): Promise<HrmsDocument> {
  const res = await api.post(`/admin/documents`, data);
  return res.data;
}

export async function updateDocument(
  id: number,
  data: { title?: string; description?: string; category_id?: number | null; tags?: string[] }
): Promise<HrmsDocument> {
  const res = await api.patch(`/admin/documents/${id}`, data);
  return res.data;
}

export async function deleteDocument(id: number): Promise<void> {
  await api.delete(`/admin/documents/${id}`);
}

// Admin-only, mirroring Sheets' updateReadonly.
export async function updateReadonly(documentId: number, enabled: boolean): Promise<HrmsDocument> {
  const res = await api.patch(`/admin/documents/${documentId}/readonly`, { enabled });
  return res.data;
}

export async function checkSimilarDocuments(title: string): Promise<SimilarDocumentMatch[]> {
  if (!title.trim()) return [];
  const res = await api.get(`/admin/documents/similar`, { params: { title } });
  return res.data.matches ?? [];
}

export class ContentConflictError extends Error {
  currentDocument: { id: number; content: string; version: number; updated_at: string | null };
  constructor(currentDocument: ContentConflictError["currentDocument"]) {
    super("This document changed since it was loaded.");
    this.currentDocument = currentDocument;
  }
}

export async function updateContent(documentId: number, content: string, version: number): Promise<HrmsDocument> {
  try {
    const res = await api.patch(`${documentsApiBase()}/${documentId}/content`, { content, version });
    return res.data;
  } catch (err: any) {
    if (err?.response?.status === 409) {
      throw new ContentConflictError(err.response.data?.current_document);
    }
    throw err;
  }
}

// ── Categories ───────────────────────────────────────────────────────────

export async function listCategories(): Promise<DocumentCategory[]> {
  const res = await api.get(`${documentsApiBase()}/categories`);
  return res.data;
}

export async function createCategory(data: { name: string; description?: string }): Promise<DocumentCategory> {
  const res = await api.post(`/admin/documents/categories`, data);
  return res.data;
}

export async function updateCategory(
  id: number,
  data: { name?: string; description?: string }
): Promise<DocumentCategory> {
  const res = await api.patch(`/admin/documents/categories/${id}`, data);
  return res.data;
}

export async function deleteCategory(id: number): Promise<void> {
  await api.delete(`/admin/documents/categories/${id}`);
}

export async function checkSimilarCategories(name: string): Promise<SimilarCategoryMatch[]> {
  if (!name.trim()) return [];
  const res = await api.get(`/admin/documents/categories/similar`, { params: { name } });
  return res.data.matches ?? [];
}

// ── Checkpoints (manual version history, admin only) ───────────────────────

export async function createCheckpoint(documentId: number, label: string | null): Promise<DocumentCheckpoint> {
  const res = await api.post(`/admin/documents/${documentId}/checkpoints`, { label });
  return res.data;
}

export async function listCheckpoints(documentId: number): Promise<DocumentCheckpoint[]> {
  const res = await api.get(`/admin/documents/${documentId}/checkpoints`);
  return res.data;
}

export async function deleteCheckpoint(documentId: number, checkpointId: number): Promise<void> {
  await api.delete(`/admin/documents/${documentId}/checkpoints/${checkpointId}`);
}

export async function purgeCheckpointsOlderThan(documentId: number, olderThanDays: number): Promise<{ deleted: number }> {
  const res = await api.delete(`/admin/documents/${documentId}/checkpoints/purge`, { params: { older_than_days: olderThanDays } });
  return res.data;
}

export async function restoreCheckpoint(documentId: number, checkpointId: number): Promise<DocumentCheckpoint> {
  const res = await api.post(`/admin/documents/${documentId}/checkpoints/${checkpointId}/restore`);
  return res.data;
}

// ── DOCX import/export ──────────────────────────────────────────────────

export async function exportDocx(documentId: number): Promise<Blob> {
  const res = await api.get(`${documentsApiBase()}/${documentId}/export-docx`, { responseType: "blob" });
  return res.data;
}

export async function importDocx(file: File, title?: string): Promise<HrmsDocument> {
  const form = new FormData();
  form.append("file", file);
  if (title) form.append("title", title);
  const res = await api.post(`/admin/documents/import-docx`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

// ── Sharing (admin only) ─────────────────────────────────────────────────

export async function listShares(documentId: number): Promise<DocumentShare[]> {
  const res = await api.get(`/admin/documents/${documentId}/shares`);
  return res.data;
}

export async function shareDocument(
  documentId: number,
  employeeId: number,
  permission: SharePermission
): Promise<DocumentShare> {
  const res = await api.post(`/admin/documents/${documentId}/shares`, { employee_id: employeeId, permission });
  return res.data;
}

export async function updateSharePermission(
  documentId: number,
  shareId: number,
  permission: SharePermission
): Promise<DocumentShare> {
  const res = await api.patch(`/admin/documents/${documentId}/shares/${shareId}`, { permission });
  return res.data;
}

export async function unshareDocument(documentId: number, shareId: number): Promise<void> {
  await api.delete(`/admin/documents/${documentId}/shares/${shareId}`);
}

export async function listAssignableEmployees(): Promise<AssignableEmployee[]> {
  const res = await api.get(`/admin/documents/assignable-employees`);
  return res.data;
}

export { isAdminBase };
